import { mgadl } from 'neuro-schemas';
import { formatTime, isPartialDate, isTime, partialDateFromDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { fromPairs } from 'ramda';
import { isSimpleObject, returnSimpleObjectArray } from './utils';

/**
 * Mapping field names for documents, maps doc to a function that provides the right field name locale id
 * If the mapping is missing, then the edithistory should be disabled for that docType
 *
 * If the mapper returns null for any field then that field is hidden
 */
export const fieldNamesMapper = (
  docType: TDocumentType,
  platform?: Platform,
): ((id: string, locale?: Locale) => string | string[] | null) | null => {
  const map = (docType: string): ((id: string, locale?: Locale) => string | string[] | null) | null => {
    switch (docType) {
      case 'mgc': {
        return (id: string) =>
          id === 'manualScore'
            ? 'functionalPerformanceMgravis.manualScore'
            : 'functionalPerformanceMgravis.labels.mgc.' + id;
      }
      case 'mgadl': {
        return (id: string, locale?: Locale) =>
          id === 'manualScore'
            ? 'functionalPerformanceMgravis.manualScore'
            : (mgadl.localizations[locale ?? 'fi'][`labels.${id}`] ?? null);
      }
      case 'diagnosticExamination': {
        return (id: string) => (id === 'code' ? null : id === 'finding' ? 'clpAndCnpTests.mgravis.findingType' : id);
      }

      case 'examination': {
        return (id: string) => 'examination.' + id;
      }
      case 'therapy': {
        return (id: string) =>
          id === 'therapyClassification' ? 'therapy.therapyClassification.title' : 'therapy.' + id;
      }
      case 'relapse': {
        return (id: string) =>
          id === 'wasCortisoneGiven'
            ? 'relapse.wasCortisoneGiven'
            : id === 'wasPlasmaGiven'
              ? 'relapse.wasPlasmaGiven'
              : id === 'cortisoneMedicationRecorded'
                ? 'relapse.cortisoneMedicationRecorded'
                : 'relapse.' + id + '.title';
      }
      case 'inflammatoryDiseaseActivityMonitoring':
      case 'inflammatoryDiseaseActivityStart': {
        return (id: string) =>
          id === 'version'
            ? 'general.version'
            : id === 'T1GadoliniumEnhancedActiveChanges'
              ? 'inflammatoryDiseaseActivity.T1GadoliniumEnhancedActiveChanges2024'
              : 'inflammatoryDiseaseActivity.' + id;
      }
      case 'sdmt': {
        return (id: string) => 'cognition.sdmt.' + id;
      }

      case 'vep':
      case 'baep':
      case 'sep': {
        return () => '';
      }
      case 'ecg': {
        return (id: string) =>
          id === 'followUpRequired' ? 'clinicalstudy.ecgFollowUpRequired' : 'clinicalstudy.' + id;
      }
      case 'heartRate':
      case 'bloodPressure':
      case 'octscan':
      case 'echocardiography': {
        if (platform === 'sma' || platform === 'dmd') return (id: string) => 'imaging.' + id;
        return (id: string) => 'clinicalstudy.' + id;
      }

      case 'comorbidity': {
        return (id: string) => {
          if (id === 'isMedicationAdverseEffect') return 'comorbidity.isAdverseEffect';
          if (id === 'medication') return 'comorbidity.whichMedication';
          return 'comorbidity.' + id;
        };
      }
      case 'sickLeave': {
        return (id: string) => 'comorbidity.' + id;
      }
      case 'pregnancy': {
        return (id: string) => 'comorbidity.' + id;
      }
      case 'contraception': {
        return (id: string) => {
          if (id === 'contraceptiveClassification') return 'comorbidity.classification';
          if (id === 'contraceptive') return 'comorbidity.methodOfContraception';
          return 'comorbidity.' + id;
        };
      }
      case 'hospitalization': {
        return (id: string) => {
          if (id === 'classification') return null; // I guess this shouldn't be shown as it can't be viewed in history. Might be random data save
          if (id === 'department') return 'comorbidity.mgravis.period';
          if (id === 'hospitalizationDetails') return 'comorbidity.additionalInformation';
          if (id === 'acuteAdverseEffect') return 'comorbidity.acuteAdverse';

          return 'comorbidity.' + id;
        };
      }

      case 'otherTreatment': {
        return (id: string) => {
          //MS D vitamin
          if (platform === 'ms') {
            if (id === 'regimen') return 'medication.regimen';
            if (id === 'type') return 'medication.otherTreatment.type';
          }
          return id === 'otherTreatmentDetails' ? 'treatment.otherTreatment.otherTreatmentDetails' : 'treatment.' + id;
        };
      }

      case 'patientDoesNotWantRespiratorySupportTherapy': {
        return (id: string) => 'treatment.' + id;
      }

      case 'hfncTherapy': {
        return (id: string) => {
          const hfncFields = ['treatmentStartLocation', 'treatmentStart'];
          if (hfncFields.includes(id)) return 'treatment.hfncTherapy.' + id;
          if (id === 'settings') return 'treatment.hfncTherapy.settings.title';
          return 'treatment.' + id;
        };
      }

      case 'diagnosis': {
        return (id: string) => {
          const smaFields = [
            'smaSMN1MutationName',
            'smaSMN1TestingMethod',
            'smaSMN2TestingMethod',
            'smaSMN1TestingMethodOther',
            'smaSMN2TestingMethodOther',
          ];
          if (smaFields.includes(id)) {
            const matchingPattern = /(\w+)(Other)/;
            const matchArr = id.match(matchingPattern);
            if (matchArr) return ['diagnosis.' + matchArr[1] + '.title', 'general.other'];
            return 'diagnosis.' + id + '.title';
          }
          if (id === 'typeOfDisease' /** MS */) return 'diagnosis.msType.title';
          if (id === 'formOfDisease' /** Mgravis */) return 'diagnosis.mgravis.formOfDisease.formOfDisease';

          return 'diagnosis.' + id;
        };
      }

      case 'hine_mm': {
        return (id: string) => 'motor.labels.hineMM.' + id;
      }
      case 'rulm': {
        return (id: string) => {
          if (
            id === 'additionalInformation' ||
            id === 'thoracolumbosacralOrthosis' ||
            id === 'thoracolumbosacralOrthosisType'
          )
            return 'motor.' + id;

          const matchingPattern = /(\w+)(Left|Right)/;
          const matchArr = id.match(matchingPattern);
          if (matchArr) {
            if (matchArr[1] === 'manualScore') return 'motor.' + id;
            return ['motor.labels.rulm.' + matchArr[1], 'motor.labels.rulm.' + id];
          }
          return 'motor.labels.rulm.' + id;
        };
      }
      case 'chop_intend': {
        return (id: string) => {
          if (id === 'manualScore') return 'motor.manualScore';
          if (id === 'hoursSinceLastFeeding') return 'motor.hoursSinceLastFeedingShort';
          const matchingPattern = /(\w+)(Left|Right|State)/;
          const matchArr = id.match(matchingPattern);
          if (matchArr) {
            return ['motor.labels.chopINTEND.' + matchArr[1], 'motor.labels.chopINTEND.' + id];
          }
          return 'motor.labels.chopINTEND.' + id;
        };
      }
      case 'hfms_e': {
        return (id: string) => {
          const basicFields = [
            'highestCurrentLevelOfMobilityInfo',
            'highestCurrentLevelOfMobility',
            'timeTakenToComplete',
          ];
          if (basicFields.includes(id)) return 'motor.' + id;

          const matchingPattern = /(\w+)(LBC)/;
          const matchArr = id.match(matchingPattern);
          if (matchArr) {
            return ['motor.labels.hfmsE.' + matchArr[1], 'motor.lbc'];
          }
          return 'motor.labels.hfmsE.' + id;
        };
      }
      case 'mfm': {
        return (id: string) => {
          const basicFields = ['mfm32OrMfm20', 'comments', 'tiredness', 'patientCooperation'];
          if (basicFields.includes(id)) return 'motor.' + id;

          if (id === 'manualD1Score') return ['motor.manuallyGivenScore', 'D1'];
          if (id === 'manualD2Score') return ['motor.manuallyGivenScore', 'D2'];
          if (id === 'manualD3Score') return ['motor.manuallyGivenScore', 'D3'];
          if (id === 'manualTotalScore') return ['motor.manuallyGivenScore', 'Total'];

          const matchingPattern = /(\w+)(Side)/;
          const matchArr = id.match(matchingPattern);
          if (matchArr) {
            return ['motor.labels.mfm.' + matchArr[1], 'motor.side'];
          }
          return 'motor.labels.mfm.' + id;
        };
      }
      case 'mwt': {
        return (id: string) => 'motor.' + id;
      }
      case 'nsaa': {
        return (id: string) => {
          if (id === 'manualScore') return 'motor.' + id;
          const matchingPattern = /(\w+)(Time)/;
          const matchArr = id.match(matchingPattern);
          if (matchArr) {
            return ['motor.labels.nsaa.' + matchArr[1], 'motor.timeSeconds'];
          }
          return 'motor.labels.nsaa.' + id;
        };
      }

      case 'generalhealth': {
        return (id: string) => 'generalhealth.' + id;
      }

      case 'mobilityAid': {
        return (id: string) => {
          if (id === 'type') return 'motorFunctionAndMobilityAid.aids.wheelChairType';
          return 'motorFunctionAndMobilityAid.aids.' + id;
        };
      }

      case 'pulmonary': {
        return (id: string) => 'pulmonary.' + id;
      }
      case 'ventilationAid': {
        return (id: string) => {
          if (id === 'type') return 'pulmonary.ventilationType';
          if (id === 'frequency') return 'pulmonary.ventilationFrequency';
          return 'pulmonary.' + id;
        };
      }
      case 'chestCircumference': {
        return (id: string) => {
          if (id === 'fullExpiration') return 'pulmonary.chestCircumferenceAtFullExpiration';
          if (id === 'fullInspiration') return 'pulmonary.chestCircumferenceAtFullInspiration';
          return 'pulmonary.' + id;
        };
      }

      case 'nocturnalPolygraphy':
      case 'enmg':
      case 'fvc': {
        return (id: string) => {
          if (id === 'sittingOrLyingDown') return 'clinicalstudy.sitOrLieDown';
          if (id === 'litres') return 'clinicalstudy.litrePlaceholder';
          if (id === 'predicted') return 'clinicalstudy.fvcPredictedPercentage';
          return 'clinicalstudy.' + id;
        };
      }

      case 'procedure': {
        return (id: string) => {
          if (id === 'code') return null; // Can't be entered/changed
          return 'procedure.' + id;
        };
      }

      case 'doctorsOrders': {
        return (id: string) => {
          if (id === 'ordererId' || id === 'registrarId' || id === 'viewerIds') return null; // IDs of who gave/inputted the order'
          if (id === 'treatmentType') return 'doctorsOrders.' + id + '.title';
          if (
            id === 'nextInterview' ||
            id === 'nextRMT' ||
            id === 'ninmtMeeting' ||
            id === 'addedToAgenda' ||
            id === 'requestFollowUp'
          )
            return 'doctorsOrders.other.' + id + '.title';

          if (id === 'rtms' || id === 'tdcs') return 'doctorsOrders.' + id + '.header';

          return 'doctorsOrders.' + id;
        };
      }

      case 'diet': {
        return (id: string) => {
          return 'dietTherapy.' + id;
        };
      }

      case 'otherSymptoms': {
        return (id: string) => {
          if (id === 'freeText') return 'otherSymptoms.mentalSymptoms.opts.other';
          return 'otherSymptoms.' + id;
        };
      }

      case 'thymusCt':
      case 'thymusMri':
      case 'muscleImaging':
      case 'cmap':
      case 'dexa':
      case 'scoliosis':
      case 'tt':
      case 'scintigraphy': // Isotooppikuvaus
      case 'pelvis': {
        return (id: string) => {
          // scintigraphy
          if (id === 'otherType') return ['imaging.imagingType', 'imaging.whatOther'];
          if (id === 'otherTracer') return ['imaging.tracer', 'imaging.whatOther'];
          // TT
          if (id === 'otherClinicallyRelevantFinding')
            return ['imaging.otherClinicallyRelevantFindingFound', 'imaging.whatOther'];

          return 'imaging.' + id;
        };
      }

      case 'mri': {
        return (id: string) => {
          //if (platform === 'sma' && id !== 'date') return null; // Sma only needs to show date field

          if (id === 'otherClinicallyRelevantFinding')
            return ['imaging.otherClinicallyRelevantFindingFound', 'imaging.whatOther'];

          if (id === 'brainVolumeTrend') return ['imaging.brainVolume', 'imaging.trend'];

          return 'imaging.' + id;
        };
      }

      case 'nutrition': {
        return (id: string) => {
          if (id === 'feedingTubes') return 'nutrition.earlierFeedingTubes';
          return 'nutrition.' + id;
        };
      }

      case 'levodopatest':
      case 'dbsEffect':
      case 'updrs_v': {
        return (id: string) => {
          // Todo: dbsEffect and levodopatest sub documents information?
          if (id === 'testType') return 'updrs.updrsOrMdsupdrs';
          if (id === 'value') return 'updrs.score';
          return 'updrs.' + id;
        };
      }
      case 'updrs_iii': {
        return (id: string) => {
          if (id === 'retrospectiveData') return null; // Maybe no need to show this
          if (id === 'testType') return 'updrs.updrsOrMdsupdrs';
          if (
            id === 'state' ||
            id === 'minutesSinceLevodopa' ||
            id === 'manualScore' ||
            id === 'dyskinesiaPresent' ||
            id === 'dyskinesiaInterference'
          )
            return 'updrs.' + id;

          const matchingPattern = /(\w+)(Right|Left|LLE|LUE|RLE|RUE|LipJaw|Neck)/;
          const matchArr = id.match(matchingPattern);
          if (matchArr && matchArr[1] && matchArr[2]) {
            return ['updrs.labels.iii.' + matchArr[1], matchArr[2]];
          }

          return 'updrs.labels.iii.' + id;
        };
      }

      case 'moca': {
        return (id: string) => {
          if (id === 'unableToPerform' || id === 'totalScoreOnly' || id === 'totalScore') return 'cognition.' + id;
          if (id === 'unableToPerformReason') return ['cognition.unableToPerform', 'general.reason'];
          return 'cognition.moca.' + id + '.title';
        };
      }
      case 'mmse': {
        return (id: string) => {
          if (id === 'unableToPerform' || id === 'totalScoreOnly' || id === 'totalScore') return 'cognition.' + id;
          if (id === 'unableToPerformReason') return ['cognition.unableToPerform', 'general.reason'];
          return 'cognition.mmse.' + id + '.title';
        };
      }
      case 'tmt': {
        return (id: string) => {
          const matchingPattern = /(\w+)(a|b)(UnableToPerform)/;
          const matchArr = id.match(matchingPattern);
          if (matchArr && matchArr[2]) {
            return ['cognition.unableToPerform', matchArr[2]];
          }
          const matchingPatternb = /(\w+)(a|b)(Time)/;
          const matchArrb = id.match(matchingPatternb);
          if (matchArrb) {
            return ['general.time', matchArrb[2] == 'b' ? 'cognition.tmt.tmtb' : 'cognition.tmt.tmta'];
          }
          return 'cognition.' + id;
        };
      }
      case 'cdr': {
        return (id: string) => {
          return 'cognition.cdr.' + id + '';
        };
      }

      case 'geneTest': {
        return (id: string) => {
          if (id === 'panelGeneMutationPairs') return 'geneTest.wesGeneMutationPairs';
          return 'geneTest.' + id;
        };
      }

      case 'cbd':
      case 'dlb':
      case 'psp':
      case 'pd': {
        return (id: string) => {
          // Todo: maybe possible to localize the field names better (PD, PSP, DLB, CBD)
          return id;
        };
      }
      case 'msa': {
        return (id: string) => {
          if (id === 'version') return 'general.version';
          // Todo: show value changes
          return 'diagnosis.msa2022.' + id + '';
        };
      }
      case 'ftd': {
        return (id: string) => {
          // Todo: show value changes
          return 'diagnosis.ftd.' + id + '.title';
        };
      }
      case 'ppa': {
        return (id: string) => {
          // Todo: show value changes
          return 'diagnosis.ppa.' + id + '.title';
        };
      }
      case 'alzheimer': {
        return (id: string) => {
          // Todo: show value changes
          return 'diagnosis.alzheimer.' + id + '.title';
        };
      }

      case 'msDiagnosticCriteria': {
        return (id: string) => {
          const ppms = [
            'progressionOfDisease',
            'periventicularArea',
            'corticalArea',
            'infratentorialArea',
            'changesInSpinalCord',
            'oligoclonalFractionsInLiquid',
          ];
          if (ppms.includes(id)) return 'diagnosis.msDiagnosticCriteria.mcDonald2017DiagnosticCriteriaPPMS.' + id;

          const other = [''];
          if (other.includes(id)) return 'diagnosis.msDiagnosticCriteria.' + id;

          if (id === 'criteria') return 'diagnosis.msDiagnosticCriteria.' + id + '.title';
          if (id === 'revision') return 'diagnosis.msDiagnosticCriteria.mcDonaldRevision.title';
          if (id === 'typicalMSSymptomPeriod')
            return 'diagnosis.msDiagnosticCriteria.mcDonald2017DiagnosticCriteriaRRMS.typicalMSSymptomPeriod.title';
          if (id === 'clinicalEvidence')
            return 'diagnosis.msDiagnosticCriteria.mcDonald2017DiagnosticCriteriaRRMS.clinicalEvidence.title';
          if (id === 'DISAndDITAnatomicNewSymptom')
            return 'diagnosis.msDiagnosticCriteria.mcDonald2017DiagnosticCriteriaRRMS.DISAndDITNewSymptom';

          if (id === 'mcDonald2010')
            return 'diagnosis.msDiagnosticCriteria.mcDonald2010DiagnosticCriteria.mcDonald2010.title';
          return 'diagnosis.msDiagnosticCriteria.mcDonald2017DiagnosticCriteriaRRMS.' + id;
        };
      }

      case 'nmosdDiagnosticCriteria': {
        return (id: string) => {
          // Todo: maybe possible to localize the field names better
          return id;
        };
      }

      case 'npi': {
        return (id: string) => {
          // Todo: show value changes
          if (id === 'latestMMSE') return 'diagnosticSurvey.npi.latestMMSEResultFromSystem';
          if (id === 'interviewee') return 'diagnosticSurvey.npi.' + id;
          return 'diagnosticSurvey.npi.' + id + '.title';
        };
      }
      case 'fbimod': {
        return (id: string) => {
          const matchingPattern = /(\w+)(Detail)/;
          const matchArr = id.match(matchingPattern);
          if (matchArr) {
            return ['diagnosticSurvey.fbimod.' + matchArr[1], 'diagnosticSurvey.fbimod.details'];
          }
          return 'diagnosticSurvey.fbimod.' + id;
        };
      }
      case 'bnsq': {
        return (id: string) => {
          return 'diagnosticSurvey.bnsq.' + id;
        };
      }
      case 'adl': {
        return (id: string) => {
          return 'diagnosticSurvey.adl.' + id + '.title';
        };
      }
      case 'iadl': {
        return (id: string) => {
          return 'diagnosticSurvey.iadl.' + id + '.title';
        };
      }

      case 'lifestyle': {
        return (id: string) => {
          return 'lifestyle.' + id + '.title';
        };
      }

      case 'background': {
        return (id: string) => {
          if (id === 'familyMembersParkinson') {
            // Return this only if parkinson is selected platform
            if (platform === 'parkinson') return ['background.relatives'];
            return null;
          }
          if (id === 'familyMembers') {
            // SMA / DMD - they use the same field
            if (platform === 'sma' || platform === 'dmd') return ['background.relatives'];
            return null;
          }
          if (id === 'familyMembersAnamnesisMS') {
            // MS
            if (platform === 'ms') return ['background.relatives'];
            return null;
          }
          if (platform !== 'ms' && id === 'familyAnamnesisMS') return null; // No need to show this field if MS not selected

          if (id === 'familyMembersAnamnesisHuntington') {
            // MS
            if (platform === 'huntington') return ['background.relatives'];
            return null;
          }

          if (id === 'workAbilityIndex') return 'background.workAbility';

          if (id === 'drivingBanR1s') return 'background.drivingBansR1';
          if (id === 'drivingBanR2s') return 'background.drivingBansR2';

          return 'background.' + id + '';
        };
      }

      case 'mgfa': {
        return (id: string) => {
          return 'diagnosis.mgravis.mgfa.' + id;
        };
      }

      case 'hifu':
      case 'thalamotomy': {
        return (id: string) => {
          if (id === 'placeOfOperation') return 'thalamotomyHifu.thalamotomy.treatmentHospital';
          if (id === 'typeOfOperation') return 'thalamotomyHifu.thalamotomy.treatmentType';
          if (id === 'treatmentTarget') return 'thalamotomyHifu.hifu.treatmentTarget';

          return 'thalamotomyHifu.' + id;
        };
      }

      case 'dbs': {
        return (id: string) => {
          if (id === 'leadRight') return 'dbs.rightLead';
          if (id === 'leadLeft') return 'dbs.leftLead';
          if (id === 'leadRightSerialNo') return ['dbs.serialNo', 'general.right'];
          if (id === 'leadLeftSerialNo') return ['dbs.serialNo', 'general.left'];
          if (id === 'technicalIssues') return 'dbs.technicalIssue';
          if (id === 'dbsEvents') return 'dbs.events';
          if (id === 'discontinuationEvents') return 'dbs.dbsDiscontinuation';
          if (id === 'generators') return 'dbs.generator';
          if (id === 'hasEnded') return 'dbs.ended';
          if (id === 'endReason') return 'dbs.endedReason';
          if (id === 'endDetails') return 'dbs.endedDetails';

          return 'dbs.' + id;
        };
      }

      case 'vns': {
        return (id: string) => {
          if (id === 'engelClassification') return 'surgicalTreatment.engelClassification.title';
          if (id === 'generators') return 'vns.generator';
          if (id === 'hasEnded') return 'vns.ending';

          return 'vns.' + id;
        };
      }

      case 'periodWithoutMedication': {
        return (id: string) => {
          return 'medication.periodWithoutMedication.' + id;
        };
      }

      case 'modifiedRankinScale': {
        return (id: string) => {
          return 'functionalPerformance.' + id;
        };
      }

      case 'modifiedChildrensGlobalAssessmentScale': {
        return (id: string) => {
          return 'functionalPerformance.' + id;
        };
      }

      case 'seizureType': {
        return (id: string) => {
          if (id === 'freeDescription') return 'diagnosis.epilepsy.seizureType.seizureFreeDescription';
          if (id === 'classification') return 'diagnosis.epilepsy.seizureType.seizureClassification';
          if (id === 'nameByPatient') return 'diagnosis.epilepsy.seizureType.seizureNameByPatient';
          return 'diagnosis.epilepsy.seizureType.' + id;
        };
      }

      case 'syndrome': {
        return (id: string) => {
          if (id === 'orphaCode') return 'diagnosis.epilepsy.orphaCode';
          if (id === 'syndromeSpecification') return 'diagnosis.epilepsy.etiology.specification';
          return 'diagnosis.epilepsy.syndrome.' + id;
        };
      }

      case 'etiology': {
        return (id: string) => {
          if (id === 'orphaCode') return 'diagnosis.epilepsy.orphaCode';
          if (id === 'subClasses') return 'diagnosis.epilepsy.etiology.classes';
          if (id === 'unknownSpecification') return 'diagnosis.epilepsy.etiology.opts.unknown';
          return 'diagnosis.epilepsy.etiology.' + id;
        };
      }

      case 'seizureFreePeriod':
      case 'seizure': {
        return (id: string) => {
          return 'seizure.' + id;
        };
      }

      case 'surgicalTreatment': {
        return (id: string) => {
          // Epilepsy
          if (id === 'operations') return 'surgicalTreatment.operation.operation';
          if (id === 'engelClassification') return 'surgicalTreatment.engelClassification.title';
          if (id === 'complications') return 'surgicalTreatment.complications.title';
          if (id === 'pads') return 'surgicalTreatment.pad.header';

          // Sleep apnea
          if (id === 'controlVisits') return 'treatment.surgicalTreatment.controls.title';
          if (id === 'controlVisitsJawSurgery') return 'treatment.surgicalTreatment.opts.jawSurgery';

          if (platform === 'epilepsy') return 'surgicalTreatment.' + id;
          if (platform === 'sleepApnea') return 'treatment.surgicalTreatment.' + id;
          return '';
        };
      }

      case 'neurologicalStatusAndEDSS': {
        return (id: string) => {
          if (id === 'calculatedEdssStep') return 'neurologicalStatusAndEDSS.calculatedEdss';
          return 'neurologicalStatusAndEDSS.' + id + '.title';
        };
      }

      case 'sleepApneaSeverity': {
        return (id: string) => {
          return 'diagnosis.sleepApnea.sleepApneaSeverity.title';
        };
      }

      case 'performanceRating': {
        return (id: string) => {
          return 'diagnosis.sleepApnea.performanceRating.title';
        };
      }

      case 'carbonDioxideMeasurement':
      case 'nightOximetry':
      case 'sleepStudy':
      case 'sleepPolygraphy': {
        return (id: string) => {
          return 'clpAndCnpTests.' + id;
        };
      }
      case 'spirometry': {
        return (id: string) => {
          return 'clpAndCnpTests.graphLocalizations.' + id;
        };
      }

      case 'checklist': {
        return (id: string) => {
          if (id === 'treatmentsPolicyDoneOther') return ['checklist.treatmentsPolicyDone', 'general.other'];

          return 'checklist.' + id;
        };
      }

      case 'oxygenTherapy': {
        return (id: string) => {
          if (id === 'treatmentResponses') return 'treatment.oxygenTherapy.treatmentResponses.title';
          if (id === 'hasEnded') return 'treatment.' + id;
          if (id === 'endReason') return 'treatment.' + id;
          if (id === 'endReasonOther') return 'treatment.' + id;
          if (id === 'settings') return 'treatment.oxygenTherapy.settings.title';

          return 'treatment.oxygenTherapy.' + id;
        };
      }
      case 'papTherapy': {
        return (id: string) => {
          if (id === 'implementationMethods') return 'treatment.papTherapy.implementationMethod';
          if (id === 'hasEnded') return 'treatment.' + id;
          if (id === 'endReason') return 'treatment.' + id;
          if (id === 'endReasonOther') return 'treatment.' + id;

          return 'treatment.papTherapy.' + id;
        };
      }
      case 'madTherapy': {
        return (id: string) => {
          if (id === 'implementationMethods') return 'treatment.madTherapy.implementationMethod';
          if (id === 'controls') return 'treatment.madTherapy.controls.title';
          if (id === 'hasEnded') return 'treatment.' + id;
          if (id === 'endReason') return 'treatment.' + id;
          if (id === 'endReasonOther') return 'treatment.' + id;

          return 'treatment.madTherapy.' + id;
        };
      }

      case 'notes': {
        return (id: string) => {
          return 'notes.' + id;
        };
      }

      case 'madrs': {
        return (id: string) => {
          if (id === 'details') return 'interviewSurveys.madrs.details';
          return 'interviewSurveys.labels.madrs.' + id;
        };
      }
      case 'gaf': {
        return (id: string) => {
          if (id === 'gafClass') return null;
          return 'interviewSurveys.gaf.' + id;
        };
      }

      case 'contraIndicationsToTreatment': {
        return (id: string) => {
          if (id === 'treatmentPeriodId') return null; // Dont need to show the parent document id
          return 'diagnosis.ninmt.contraIndicationsToTreatment.' + id;
        };
      }

      case 'symptomsAndLocations': {
        return (id: string) => {
          if (id === 'treatmentPeriodId') return null; // Dont need to show the parent document id
          if (id === 'averageSymptomIntensity') return 'diagnosis.ninmt.symptomsAndLocations.averagePainTitle';
          if (id === 'averageSymptomHarm') return 'diagnosis.ninmt.symptomsAndLocations.averageHarmTitle';
          return 'diagnosis.ninmt.symptomsAndLocations.' + id;
        };
      }

      case 'rtms': {
        return (id: string) => {
          if (id === 'unusedSessions') return 'rtms.unusedSession';
          if (id === 'hasEnded') return 'rtms.ending';
          return 'rtms.' + id;
        };
      }
      case 'tdcs': {
        return (id: string) => {
          if (id === 'otherEvents') return 'rtms.events';
          if (id === 'unusedSessions') return 'rtms.unusedSession';
          if (id === 'hasEnded') return 'rtms.ending';
          return 'tdcs.' + id;
        };
      }

      case 'uhdrsConfidenceIndex': {
        return (id: string) => {
          if (id === 'index') return 'diagnosis.huntington.uhdrs.history.index';
          return 'diagnosis.huntington.uhdrs.' + id;
        };
      }

      case 'uhdrsMotorRatingScale': {
        return (id: string) => {
          if (id === 'totalScoreOnly') return 'motorFunctionHuntington.uhdrsTotalScoreOnly';
          if (id === 'manualScore') return 'motorFunctionHuntington.uhdrsMotorManualScore';
          if (id === 'shortTest') return 'motorFunctionHuntington.uhdrsShortTestSelected';

          return 'motorFunctionHuntington.labels.uhdrsMotorRatingScale.' + id;
        };
      }

      case 'pbaS': {
        return (id: string) => {
          if (id === 'generalComments' || id === 'isInformantRelative' || id === 'isInformantHouseholdMember')
            return 'behaviour.pbaS.' + id;

          return id;
        };
      }

      case 'uhdrsFunctionalAssessment': {
        return (id: string) => {
          if (id === 'generalComments' || id === 'isInformantRelative' || id === 'isInformantHouseholdMember')
            return 'behaviour.pbaS.' + id;

          return 'functionalPerformance.labels.uhdrsFunctionalAssessment.' + id;
        };
      }
      case 'uhdrsIndependenceScale': {
        return (id: string) => {
          return 'functionalPerformance.uhdrsIndependenceScale';
        };
      }

      case 'uhdrsFunctionalCapacity': {
        return (id: string) => {
          return 'functionalPerformance.labels.uhdrsFunctionalCapacity.' + id;
        };
      }

      case 'motorfunction': {
        return (id: string) => {
          const matchingPattern = /(\w+)(Start|End)/;
          const matchArr = id.match(matchingPattern);
          if (matchArr && matchArr[1] && matchArr[2])
            return ['motorFunctionAndMobilityAid.motorfunction.' + matchArr[1], matchArr[2]];

          return 'motorFunctionAndMobilityAid.motorfunction.' + id;
        };
      }

      case 'parkinsonSymptoms': {
        return (id: string) => {
          if (id === 'parkinsonEarlySymptom') return 'diagnosis.parkinsonEarlySymptom.title';
          if (id === 'parkinsonMotoricSymptomOnset') return 'diagnosis.parkinsonMotoricSymptomOnset.title';
          if (id === 'parkinsonMotoricSymptomOnsetSide') return 'diagnosis.parkinsonMotoricSymptomOnsetSide.title';
          return 'diagnosis.' + id;
        };
      }

      case 'msSymptoms': {
        return (id: string) => {
          if (id === 'msSymptoms') return 'diagnosis.msSymptom.title';
          if (id === 'msSymptomLevel') return 'diagnosis.msSymptomLevel.title';
          if (id === 'msOriginOfInformation') return 'diagnosis.msOriginOfInformation.title';
          return 'diagnosis.' + id;
        };
      }

      case 'mgravisSymptoms': {
        return (id: string) => {
          if (id === 'age') return 'diagnosis.mgravis.symptoms.age';
          if (id === 'symptomLocation') return 'diagnosis.mgravis.symptoms.location';
          return 'diagnosis.' + id;
        };
      }

      case 'sleepApneaFirstVisit': {
        return (id: string) => {
          if (id === 'referralOrAcute') return 'diagnosis.sleepApnea.sleepApneaFirstVisit.referralOrAcute';
          if (id === 'dateOfReferral') return 'diagnosis.sleepApnea.sleepApneaFirstVisit.dateOfReferral';
          if (id === 'dateOfFirstVisit') return 'diagnosis.sleepApnea.sleepApneaFirstVisit.dateOfFirstVisit';
          return 'diagnosis.' + id;
        };
      }
      case 'respiratoryFirstVisit': {
        return (id: string) => {
          if (id === 'referralOrAcute') return 'diagnosis.sleepApnea.respiratoryFirstVisit.referralOrAcute';
          if (id === 'dateOfReferral') return 'diagnosis.sleepApnea.respiratoryFirstVisit.dateOfReferral';
          if (id === 'dateOfFirstVisit') return 'diagnosis.sleepApnea.respiratoryFirstVisit.dateOfFirstVisit';
          return 'diagnosis.' + id;
        };
      }

      case 'huntingtonSymptoms': {
        return (id: string) => {
          if (id === 'additionalInformation') return 'diagnosis.huntingtonSymptom.additionalInformation';
          if (id === 'symptoms') return 'diagnosis.huntingtonSymptoms';
          return 'diagnosis.' + id;
        };
      }

      case 'symptomsDetails': {
        return (id: string) => {
          if (id === 'treatmentPeriodId') return null;
          if (id === 'symptomsDetails') return 'diagnosis.ninmt.symptomsDetails.title';
          return 'diagnosis.' + id;
        };
      }

      case 'referringPhysicianAndUnit': {
        return (id: string) => {
          if (id === 'treatmentPeriodId') return null;
          if (id === 'referringPhysician') return 'diagnosis.ninmt.referringPhysicianAndUnit.referringPhysician';
          if (id === 'unit') return 'diagnosis.ninmt.referringPhysicianAndUnit.unit';
          return 'diagnosis.' + id;
        };
      }

      case 'indicationForTreatment': {
        return (id: string) => {
          if (id === 'treatmentPeriodId') return null;
          if (id === 'indication') return 'diagnosis.ninmt.indicationForTreatment.title';
          if (id === 'painSpecification') return 'diagnosis.ninmt.indicationForTreatment.opts.pain';
          if (id === 'neuropathicPainFinding') return 'diagnosis.ninmt.indicationForTreatment.neuropathicFinding';
          if (id === 'neuropathicPainFindingCertainty')
            return 'diagnosis.ninmt.indicationForTreatment.opts.neuropathicPain';
          if (id === 'painICD10') return 'profile.opts.ICD-10';
          return 'diagnosis.ninmt.indicationForTreatment.' + id;
        };
      }

      case 'htt': {
        return (id: string) => {
          if (id === 'status') return 'Status';
          return 'diagnosis.huntington.htt.' + id;
        };
      }

      case 'cerad': {
        return (id: string) => {
          if (id === 'version') return 'general.version';
          if (id === 'additionalInformation') return 'general.optional';
          return 'cognition.cerad.' + id;
        };
      }

      case 'medication': {
        return (id: string) => {
          if (id === 'medicationName') return 'medication.medication';
          if (id === 'medicationSubstances') return 'medication.substances';
          if (id === 'dosageForm') return null;
          if (id === 'endReasonPrimary') return ['medication.ended', 'medication.endedReasonPrimary'];
          if (id === 'administration') return 'medication.administration.administrations';
          if (id === 'hasEnded') return 'medication.ended';
          if (id === 'endReasonOther') return ['medication.ended', 'medication.endedReasonOther'];
          if (id === 'endTime') return 'medication.endedTime';
          if (id === 'endReason') return 'medication.endedReason';
          if (id === 'strengths') return 'medication.doses.strengths';
          if (id === 'isClinicalStudy') return 'medication.clinicalStudyMedication';
          if (id === 'identifyingHash') return null; // I guess this field is something that is added sometimes from integrations
          return 'medication.' + id;
        };
      }

      case 'ariaSymptoms': {
        return (id: string) => {
          return 'ariaSymptoms.' + id;
        };
      }

      case 'ninmtTreatmentPeriod': {
        return (id: string) => {
          return 'diagnosis.' + id;
        };
      }

      case 'adverseEffect': {
        return (id: string) => {
          if (id === 'medicationId') return null;
          return 'medication.' + id;
        };
      }

      case 'ftmtrs': {
        return (id: string) => {
          const matchingPattern = /(\w+)(right|left)/;
          const matchArr = id.match(matchingPattern);
          if (matchArr?.[1] && matchArr[2]) return ['updrs.labels.ftmtrs.' + matchArr[1], matchArr[2]];

          return 'updrs.labels.ftmtrs.' + id;
        };
      }

      case 'filestorefile': {
        return (id: string) => {
          if (id === 'fileContentId' || id === 'checksum') return null;
          return 'patientFiles.' + id;
        };
      }

      default: {
        return null;
      }
    }
  };

  const mapHasDoctype = map(docType);
  if (!mapHasDoctype) return null;

  return (id: string, locale?: Locale) => {
    if (id === 'date' || id === 'startDate') return 'general.date';
    if (id === 'endDate') {
      if (docType === 'comorbidity') return 'comorbidity.recoveryDate';
      return 'general.endDate';
    }
    if (id === 'time' || id === 'startTime') return 'general.time';
    return map(docType)!(id, locale);
  };
};

/** Map specific localizations for field values if needed  */
export const valueMapper = (
  docType: string,
  platform?: Platform,
): ((fieldName: string, value: TFieldValue) => TFieldValue) | null => {
  const map = (docType: string): ((fieldName: string, value: TFieldValue) => TFieldValue) | null => {
    switch (docType) {
      case 'diagnosticExamination': {
        return (fieldName, value) => {
          if (fieldName === 'finding' && isSimpleObject(value)) {
            return fromPairs(
              Object.keys(value).map(key => {
                return [
                  `clpAndCnpTests.mgravis.${key === 'type' ? 'findingType' : key === 'supportDiagnosis' ? 'supportDiagnosis' : 'abnormalDetails'}`,
                  value[key] === 'abnormal' || value[key] === 'normal'
                    ? 'clpAndCnpTests.mgravis.opts.' + value[key]
                    : value[key] === true
                      ? 'general.yes'
                      : value[key] === false
                        ? 'general.no'
                        : value[key],
                ];
              }),
            );
          }
          return null;
        };
      }
      case 'therapy': {
        return (fieldName, value) =>
          fieldName === 'therapyClassification'
            ? 'therapy.therapyClassification.opts.' + value
            : fieldName === 'therapyTypes'
              ? 'therapy.opts.' + value
              : null;
      }
      case 'relapse': {
        return (fieldName, value) =>
          fieldName === 'symptoms'
            ? Array.isArray(value) && value.map(val => 'relapse.symptoms.opts.' + val)
            : fieldName === 'symptomLevel'
              ? Array.isArray(value) && value.map(val => 'relapse.symptomLevel.opts.' + val)
              : fieldName === 'originOfInformation'
                ? 'relapse.originOfInformation.opts.' + value
                : null;
      }
      case 'inflammatoryDiseaseActivityMonitoring':
      case 'inflammatoryDiseaseActivityStart': {
        return (fieldName, value) =>
          fieldName === 'version' ? value : fieldName !== 'date' ? 'inflammatoryDiseaseActivity.opts.' + value : null;
      }
      case 'sdmt': {
        return (fieldName, value) =>
          fieldName === 'totalScoreOnly' && Array.isArray(value) && typeof value[0] === 'string'
            ? 'general.yes'
            : fieldName === 'testType'
              ? 'cognition.sdmt.' + value
              : null;
      }
      case 'octscan':
      case 'echocardiography':
      case 'ecg': {
        return (fieldName, value) =>
          fieldName === 'result' ? 'clinicalstudy.opts.normalBorderlineAbnormal.' + value : null;
      }
      case 'comorbidity': {
        return (fieldName, value) => {
          if (fieldName === 'classification' && isSimpleObject(value)) {
            return value['icd10code'] + ' - ' + value['icd10description'];
          }
          if (fieldName === 'sicknessEffectOnRespiratoryFailure')
            return 'comorbidity.opts.sicknessEffectOnRespiratoryFailure.' + value;

          // Skoliosis
          if (fieldName === 'corsetTreatments' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              { corsetType: 'comorbidity.corsetType' },
              {
                corsetType: {
                  hardBrace: 'comorbidity.opts.corsetType.hardBrace',
                  softBrace: 'comorbidity.opts.corsetType.softBrace',
                },
              },
            );
          // Skoliosis
          if (fieldName === 'backSurgeries' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                instrumentation: 'comorbidity.instrumentation',
                instrumentationAdditionalInformation: 'comorbidity.opts.instrumentation.other',
                surgeryLevel: 'comorbidity.surgeryLevel',
                surgeryTechnique: 'comorbidity.surgeryTechnique',
              },
              {
                surgeryTechnique: {
                  expandableBackInstrumentation: 'comorbidity.opts.surgeryTechnique.expandableBackInstrumentation',
                  definitiveFusion: 'comorbidity.opts.surgeryTechnique.definitiveFusion',
                },
                instrumentation: {
                  magneticRods: 'comorbidity.opts.instrumentation.magneticRods',
                  shilla: 'comorbidity.opts.instrumentation.shilla',
                  other: 'comorbidity.opts.instrumentation.other',
                },
              },
            );
          // Q65
          if (fieldName === 'hipSurgeries' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              { surgeryType: 'comorbidity.surgeryType' },
              {
                surgeryType: {
                  hipVariation: 'comorbidity.opts.surgeryType.hipVariation',
                  pelvicOsteotomy: 'comorbidity.opts.surgeryType.pelvicOsteotomy',
                },
              },
            );

          return null;
        };
      }
      case 'sickLeave': {
        return (fieldName, value) =>
          fieldName === 'reasonForSickLeave' ? 'comorbidity.opts.reasonForSickLeave.' + value : null;
      }
      case 'otherTreatment': {
        return (fieldName, value) => {
          if (platform === 'ms') {
            if (fieldName === 'startDate') return null;
            if (fieldName === 'type') return 'medication.otherTreatment.opts.' + value;
            if (fieldName === 'endReason' && Array.isArray(value)) return value.map(val => 'medication.opts.' + val);
            if (fieldName === 'endReasonOther') return value;
            if (fieldName === 'regimen' && Array.isArray(value))
              return returnSimpleObjectArray(
                value,
                {
                  frequency: 'medication.otherTreatment.frequency',
                  dosage: 'medication.otherTreatment.dosage',
                  otherFrequency: 'medication.otherTreatment.otherFrequency',
                },
                {
                  frequency: val => 'medication.otherTreatment.opts.' + val,
                },
              );
          }

          return fieldName === 'otherTreatmentDetails' || fieldName === 'date' || fieldName === 'endDate'
            ? null
            : fieldName === 'hasEnded' && Array.isArray(value)
              ? value[0] === true
                ? 'general.yes'
                : value[0] === false
                  ? 'general.no'
                  : null
              : 'treatment.otherTreatment.opts.' + value;
        };
      }
      case 'contraception': {
        return (fieldName, value) => {
          if (fieldName === 'contraceptiveClassification')
            return 'comorbidity.opts.contraceptiveClassification.' + value;
          if (fieldName === 'contraceptive') return 'comorbidity.opts.contraceptive.' + value;
          return null;
        };
      }
      case 'hospitalization': {
        return (fieldName, value) => {
          if (fieldName === 'hospitalizationType') return 'comorbidity.opts.' + value;
          if (fieldName === 'department') return 'comorbidity.mgravis.' + value;
          return null;
        };
      }

      case 'diagnosis': {
        return (fieldName, value) => {
          const smaFields = ['smaSMN1MutationName', 'smaSMN1TestingMethod', 'smaSMN2TestingMethod'];
          if (smaFields.includes(fieldName)) {
            return `diagnosis.${fieldName}.opts.${value}`;
          }

          if (fieldName === 'typeOfDisease' && Array.isArray(value)) {
            return returnSimpleObjectArray(value, { type: 'diagnosis.msType.title' }, {});
          }
          if (fieldName === 'formOfDisease' && Array.isArray(value)) {
            return returnSimpleObjectArray(
              value,
              { formOfDisease: 'diagnosis.mgravis.formOfDisease.formOfDisease' },
              {
                formOfDisease: {
                  generalized: 'diagnosis.mgravis.formOfDisease.generalized',
                  ocular: 'diagnosis.mgravis.formOfDisease.ocular',
                },
              },
            );
          }

          if (fieldName === 'diagnosis') return 'diagnosis.opts.' + value;
          return null;
        };
      }

      case 'hfms_e': {
        return (fieldName, value) => {
          if (
            fieldName === 'highestCurrentLevelOfMobility' ||
            fieldName === 'tiredness' ||
            fieldName === 'mfm32OrMfm20'
          )
            return 'motor.opts.hfmsE.' + value;
          return null;
        };
      }

      case 'mfm': {
        return (fieldName, value) => {
          if (fieldName === 'patientCooperation' || fieldName === 'tiredness' || fieldName === 'mfm32OrMfm20')
            return 'motor.opts.mfm.' + value;
          return null;
        };
      }

      case 'generalhealth': {
        return (fieldName, value) => {
          if (fieldName === 'clinicianRating') return 'generalhealth.opts.clinicianRating.' + value;
          if (fieldName === 'patientRating') return 'generalhealth.opts.patientRating.' + value;
          return null;
        };
      }

      case 'mobilityAid': {
        return (fieldName, value) => {
          if (fieldName === 'type' || fieldName === 'aidType' || fieldName === 'use')
            return 'motorFunctionAndMobilityAid.aids.opts.' + value;
          return null;
        };
      }

      case 'pulmonary': {
        return (fieldName, value) => {
          if (value === 'no' || value === 'daily' || value === 'onDemand')
            return 'pulmonary.opts.noDailyOnDemand.' + value;
          return null;
        };
      }
      case 'ventilationAid': {
        return (fieldName, value) => {
          if (fieldName === 'type' || fieldName === 'frequency') return 'pulmonary.opts.' + value;
          return null;
        };
      }
      case 'chestCircumference': {
        return (fieldName, value) => {
          if (fieldName === 'type' || fieldName === 'frequency') return 'pulmonary.opts.' + value;
          return null;
        };
      }

      case 'fvc': {
        return (fieldName, value) => {
          if (fieldName === 'sittingOrLyingDown') return 'clinicalstudy.opts.' + value;
          return null;
        };
      }

      case 'scintigraphy':
      case 'tt':
      case 'thymusCt':
      case 'thymusMri': {
        return (fieldName, value) => {
          // scintigraphy
          if (fieldName === 'imagingType') return 'imaging.opts.imagingType.' + value;
          if (fieldName === 'tracer') return 'imaging.opts.tracer.' + value;
          if (fieldName === 'result') return 'imaging.opts.result.' + value;
          if (fieldName === 'symmetryOfFinding') return 'imaging.opts.symmetricAsymmetric.' + value;
          // TT
          if (fieldName === 'atrophy') return 'imaging.opts.atrophy.' + value;
          // Thymus
          if (fieldName === 'finding' && Array.isArray(value)) return value.map(v => 'imaging.opts.finding.' + v);
          if (fieldName === 'finding' && typeof value === 'string') return 'imaging.opts.finding.' + value;
          return null;
        };
      }
      case 'mri': {
        return (fieldName, value) => {
          // Parkinson
          if (fieldName === 'atrophy') return 'imaging.opts.atrophy.' + value;
          if (fieldName === 'fazekasGrade') return 'imaging.opts.fazekas.' + value;
          if (fieldName === 'wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous')
            return 'imaging.opts.yesFazekas0.' + value;

          // TODO: MS value objects
          if (fieldName === 'brainVolumeTrend') return 'imaging.opts.decreasedSameIncreased.' + value;
          return null;
        };
      }

      case 'diet': {
        return (fieldName, value) => {
          if (fieldName === 'reason' && Array.isArray(value)) return value.map(v => 'dietTherapy.opts.' + v);
          if (fieldName === 'diet' && typeof value === 'string') return 'dietTherapy.opts.' + value;
          return null;
        };
      }

      case 'otherSymptoms': {
        return (fieldName, value) => {
          if (fieldName === 'classification') return 'otherSymptoms.' + value + '.title';
          if (fieldName === 'symptoms') return value; // Todo: The localization depends on the classification field

          return null;
        };
      }

      case 'nutrition': {
        return (fieldName, value) => {
          if (fieldName === 'feedingTubes' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              { feedingTubeType: 'nutrition.feedingTubeType', feedingTubePractice: 'nutrition.feedingTubePractice' },
              {
                feedingTubeType: {
                  nasalFeedingTube: 'nutrition.opts.nasalFeedingTube',
                  gastricFeedingTube: 'nutrition.opts.gastricFeedingTube',
                  poAutonomously: 'nutrition.opts.poAutonomously',
                  poAssisted: 'nutrition.opts.poAssisted',
                },
                feedingTubePractice: {
                  onlyWithTube: 'nutrition.opts.onlyWithTube',
                  inAdditionWithTube: 'nutrition.opts.inAdditionWithTube',
                },
              },
            );
          return null;
        };
      }

      case 'moca': {
        return (fieldName, value) => {
          if (Array.isArray(value) && value.length === 1 && value[0] === 'totalScoreOnly') return 'general.yes';
          if (Array.isArray(value) && value.length === 1 && value[0] === 'yes') return 'general.yes';
          if (Array.isArray(value) && value.every(val => typeof val !== 'number'))
            return value.map(v => 'cognition.moca.' + fieldName + '.' + v);
          return null;
        };
      }
      case 'mmse': {
        return (fieldName, value) => {
          if (Array.isArray(value) && value.length === 1 && value[0] === 'totalScoreOnly') return 'general.yes';
          if (Array.isArray(value) && value.length === 1 && value[0] === 'yes') return 'general.yes';
          if (Array.isArray(value) && value.every(val => typeof val !== 'number'))
            return value.map(v => 'cognition.mmse.' + fieldName + '.' + v);
          return null;
        };
      }
      case 'tmt': {
        return (fieldName, value) => {
          if (Array.isArray(value) && value.length === 1 && value[0] === 'unableToPerform') return 'general.yes';

          return null;
        };
      }

      case 'geneTest': {
        return (fieldName, value) => {
          if (fieldName === 'genesIncluded' && Array.isArray(value)) return value.map(val => 'geneTest.opts.' + val);
          if (fieldName === 'mutations' && Array.isArray(value)) return value;
          if (
            (fieldName === 'panelGeneMutationPairs' ||
              fieldName === 'wesGeneMutationPairs' ||
              fieldName === 'wesGeneVusFindingPairs') &&
            Array.isArray(value)
          )
            return returnSimpleObjectArray(
              value,
              {
                gene: 'geneTest.gene',
                vusFinding: 'geneTest.vusFinding',
                mutation: 'geneTest.mutation',
                otherGene: 'geneTest.whichOtherGene',
              },
              {/** Todo maybe localize gene options here, or develop function for it */},
            );
          if (fieldName === 'gene' || fieldName === 'geneTestType') return 'geneTest.opts.' + value;

          return null;
        };
      }

      case 'npi': {
        return (fieldName, value) => {
          if (fieldName === 'interviewee') return 'diagnosticSurvey.npi.opts.interviewee.' + value;
          return null;
        };
      }
      case 'fbimod': {
        return (fieldName, value) => {
          if (fieldName === 'respondent') return 'diagnosticSurvey.fbimod.opts.' + value;
          return null;
        };
      }
      case 'bnsq': {
        return (fieldName, value) => {
          const noLocalizationFields = [
            'goingToSleepTimeWeek',
            'goingToSleepTimeWeekend',
            'prescriptionSleepMedicationPast3MonthsWhich',
            'wakingUpTimeWeek',
            'wakingUpTimeWeekend',
            'detailsOnSleepAndHelpNeeded',
          ];
          if (fieldName === 'date' || typeof value === 'number' || noLocalizationFields.includes(fieldName))
            return null;
          return 'diagnosticSurvey.bnsq.opts.' + value;
        };
      }
      case 'iadl': {
        return (fieldName, value) => {
          if (fieldName === 'date') return null;
          return 'diagnosticSurvey.iadl.' + fieldName + '.opts.' + value;
        };
      }

      case 'lifestyle': {
        return (fieldName, value) => {
          if (
            isPartialDate(value) ||
            typeof value === 'number' ||
            fieldName === 'whichOtherDiet' ||
            fieldName === 'howMuchNicotineProductsUsed' ||
            fieldName === 'whichOtherIntoxicant'
          )
            return null;
          if (fieldName === 'specialDiets' && Array.isArray(value))
            return value.map(val => 'lifestyle.specialDiets.' + val);
          return 'lifestyle.' + fieldName + '.' + value;
        };
      }

      case 'background': {
        return (fieldName, value) => {
          const parkinsonGeneralValueFields = [
            'familyPrevalenceDisease',
            'familyPrevalenceParkinson',
            'date',
            'driversLicense',
            'earlierHeadInjury',
            'powerOfAttorney',
            'powerOfAttorneyStart',
            'careAllowance',
            'firearmsLicense',
          ];

          const smadmdGeneralValueFields = ['familyMemberDiagnosedDMD', 'familyMemberDiagnosedSMA'];

          const msGeneralValueFields = [
            'employmentOther',
            'reimbursementValidUntil',
            'familyCarer',
            'personalAssistant',
            'sterilization',
            'familyAnamnesisMS',
          ];

          const sleepapneaGeneralValueFields = [
            'nicotineProductsUsage',
            'professionalDriving',
            'drivingTestDate',
            'snusStart',
            'snusEnd',
            'nicotineProductsStart',
            'nicotineProductsEnd',
            'otherIntoxicants',
          ];

          const epilepsyGeneralValueFields = [
            'otherFamilyAnamnesis',
            'complicationInfo',
            'feverCrampsInfo',
            'relativityInfo',
            'perinatalComplications',
            'feverCramps',
            'disabledDiagnosis',
            'epilepsyFirstDegreeRelative',
          ];

          const huntingtonGeneralValueFields = [
            'relativeDegree2FreeText',
            'familyAnamnesisHuntington',
            'retirementDate',
            'contactPermit',
          ];

          if (fieldName === 'familyMembersParkinson') return 'background.relatives';
          if (fieldName === 'earlierHeadInjuryYear') return value;

          if (fieldName === 'employment' && Array.isArray(value))
            return value.map(val => 'background.opts.employment.' + val);
          if (fieldName === 'familyMembers' && Array.isArray(value))
            return value.map(val => 'background.opts.relatives.' + val);

          if (fieldName === 'familyMembersAnamnesisMS' && Array.isArray(value))
            return value.map(val => 'background.opts.familyMembers.' + val);

          if (fieldName === 'familyMembersAnamnesisHuntington' && Array.isArray(value))
            return value.map(val => 'background.opts.relatives.' + val);

          if (fieldName === 'driversLicenseGroup' && Array.isArray(value))
            return value.map(val => 'background.opts.driversLicenseGroup.' + val);
          if ((fieldName === 'drivingBanR1s' || fieldName === 'drivingBanR2s') && Array.isArray(value))
            return returnSimpleObjectArray(value, {}, {});
          if (fieldName === 'noProDrivingRequirements' && Array.isArray(value))
            return returnSimpleObjectArray(value, {}, {});

          if ((fieldName === 'pregnancies' || fieldName === 'nursing') && Array.isArray(value))
            return returnSimpleObjectArray(value, {}, {});

          if (fieldName === 'education' && Array.isArray(value))
            return value.map(val => 'background.opts.education.' + val);

          if (fieldName === 'whichIntoxicants' && Array.isArray(value))
            return value.map(val => 'background.opts.whichIntoxicants.' + val);

          if (fieldName === 'relatives' && Array.isArray(value))
            return value.map(val => 'background.opts.relatives.' + val);

          if (fieldName === 'dailyActivitiesAbility') return 'background.opts.huntingtonImpairment.' + value;
          if (fieldName === 'financeHandlingAbility') return 'background.opts.huntingtonImpairment.' + value;
          if (fieldName === 'householdChoresAbility') return 'background.opts.huntingtonImpairment.' + value;

          if (
            fieldName === 'workAbilityIndex' &&
            value &&
            typeof value === 'object' &&
            'selfAssessmentAbilityToWorkIn2Years' in value
          )
            return (
              'background.opts.workAbilityIndex.selfAssessmentAbilityToWorkIn2Years.' +
              value['selfAssessmentAbilityToWorkIn2Years']
            );

          if (
            typeof value === 'number' ||
            parkinsonGeneralValueFields.includes(fieldName) ||
            smadmdGeneralValueFields.includes(fieldName) ||
            msGeneralValueFields.includes(fieldName) ||
            sleepapneaGeneralValueFields.includes(fieldName) ||
            epilepsyGeneralValueFields.includes(fieldName) ||
            huntingtonGeneralValueFields.includes(fieldName)
          )
            return null;
          return 'background.opts.' + fieldName + '.' + value;
        };
      }

      case 'nmosdDiagnosticCriteria': {
        return (fieldName, value) => {
          if (fieldName === 'AQP4IgGResult')
            return 'diagnosis.nmosdDiagnosticCriteria.aqp4.AQP4IgGResult.opts.' + value;
          return null;
        };
      }

      case 'hifu':
      case 'thalamotomy': {
        return (fieldName, value) => {
          if (fieldName === 'typeOfOperation') return 'thalamotomyHifu.thalamotomy.opts.' + value;

          if (fieldName === 'treatmentTarget') return 'thalamotomyHifu.hifu.opts.' + value;

          if (fieldName === 'adverseEffects' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                additionalInformation: 'thalamotomyHifu.adverseEffectAdditionalInformation',
                adverseEffects: 'thalamotomyHifu.adverseEffects',
              },
              { adverseEffects: value => 'thalamotomyHifu.opts.' + value },
            );
          return null;
        };
      }

      case 'dbs': {
        return (fieldName, value) => {
          const dbsFields = ['removalReason', 'status', 'endReason'];
          if (dbsFields.includes(fieldName)) return 'dbs.opts.' + value;
          if (fieldName === 'removalType' && Array.isArray(value)) return value.map(val => 'dbs.opts.' + val);
          if (fieldName === 'technicalIssues' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              { issueDetails: 'dbs.issueDetails', issueType: 'dbs.issueTypeShort' },
              {
                issueType: val => 'dbs.opts.' + val,
              },
            );
          if (fieldName === 'generators' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                firstGenerator: null,
                generatorDate: 'dbs.generatorDate',
                generator: 'dbs.generator',
                generatorSerialNo: 'dbs.generatorSerialNo',
                generatorNextChangeDate: 'dbs.nextChange',
                rechargeable: 'dbs.rechargeable',
              },
              {},
            );
          if (fieldName === 'discontinuationEvents' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                discontinuationDetails: 'dbs.discontinuationDetails',
                discontinuationReason: 'dbs.discontinuationReason',
                discontinuationType: 'dbs.discontinuationType',
              },
              {
                discontinuationReason: val => 'dbs.opts.' + val,
                discontinuationType: val => 'dbs.opts.' + val,
              },
            );
          if (fieldName === 'dbsEvents' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                eventDetails: 'dbs.eventDetails',
                eventTypes: 'dbs.eventTypeShort',
                otherDetails: 'dbs.whichOtherEvent',
              },
              {
                eventTypes: val => 'dbs.opts.' + val,
              },
            );

          return null;
        };
      }

      case 'vns': {
        return (fieldName, value) => {
          const vnsFields = ['removalReason', 'status', 'endReason', 'electrodeMountingLocation', 'removalType'];
          if (vnsFields.includes(fieldName)) return 'vns.opts.' + fieldName + '.' + value;
          if (fieldName === 'removalType' && Array.isArray(value)) return value.map(val => 'vns.opts.' + val);

          if (fieldName === 'generators' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                generator: 'vns.generator',
                generatorSerialNo: 'vns.generatorSerialNo',
                generatorNextChangeDate: 'vns.generatorNextChangeDate',
              },
              {
                eventTypes: val => 'vns.opts.' + val,
              },
            );

          if (fieldName === 'discontinuations' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                additionalInformation: 'vns.discontinuationAdditionalInformation',
                reason: 'vns.discontinuationReason',
                type: 'vns.discontinuationType',
              },
              {
                reason: val => 'vns.opts.discontinuationReason.' + val,
                type: val => 'vns.opts.discontinuationType.' + val,
              },
            );

          if (fieldName === 'technicalIssues' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                issue: 'vns.technicalIssue',
                additionalInformation: 'vns.technicalIssueAdditionalInformation',
              },
              {
                issue: val => 'vns.opts.technicalIssue.' + val,
              },
            );

          if (fieldName === 'magnetModeActivations' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                activationsPerDay: 'vns.activationsPerDay',
                additionalInformation: 'vns.additionalInformation',
              },
              {},
            );

          if (fieldName === 'autoStimModeActivations' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                activationsPerDay: 'vns.activationsPerDay',
                additionalInformation: 'vns.additionalInformation',
              },
              {},
            );

          if (fieldName === 'adverseEffects' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                effects: 'vns.activationsPerDay',
                additionalInformation: 'vns.additionalInformation',
              },
              {
                effects: val => 'vns.opts.adverseEffect.' + val,
              },
            );
          if (fieldName === 'postoperativeComplications' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                complications: 'vns.complications',
                additionalInformation: 'vns.additionalInformation',
              },
              {
                complications: val => 'vns.opts.postoperativeComplication.' + val,
              },
            );

          // TODO settings
          // if (fieldName === 'settings' && Array.isArray(value))
          //   return returnSimpleObjectArray(
          //     value,
          //     {
          //       generator: 'vns.generator',
          //       efficiencyCycle: 'vns.efficiencyCycle',
          //       tachycardiaDetection: 'vns.tachycardiaDetection',
          //       heartRateDetectionSetting: 'vns.heartRateDetectionSetting',
          //       outputCurrent: 'vns.outputCurrent',
          //       rate: 'vns.rate',
          //       pulseWidth: 'vns.pulseWidth',
          //       onTime: 'vns.onTime',
          //       offTime: 'vns.offTime',
          //       autoStimThreshold: 'vns.autoStimThreshold',
          //     },
          //     {
          //       issue: val => 'vns.opts.technicalIssue.' + val,
          //     },
          //   );

          return null;
        };
      }

      case 'periodWithoutMedication': {
        return (fieldName, value) => {
          if (fieldName === 'reason') return 'medication.periodWithoutMedication.opts.' + value;

          return null;
        };
      }

      case 'modifiedRankinScale': {
        return (fieldName, value) => {
          if (fieldName === 'rating') return 'functionalPerformance.opts.modifiedRankinScale.rating.' + value;
          return null;
        };
      }

      case 'modifiedChildrensGlobalAssessmentScale': {
        return (fieldName, value) => {
          if (fieldName === 'rating')
            return 'functionalPerformance.opts.modifiedChildrensGlobalAssessmentScale.rating.' + value;
          return null;
        };
      }

      case 'seizureType': {
        return (fieldName, value) => {
          if (fieldName === 'classification')
            return value &&
              typeof value === 'object' &&
              'classification' in value &&
              typeof value.classification === 'string'
              ? value.classification
              : '';
          return null;
        };
      }

      case 'syndrome': {
        return (fieldName, value) => {
          if (fieldName === 'syndrome') return 'diagnosis.epilepsy.syndrome.opts.' + value;
          return null;
        };
      }

      case 'etiology': {
        return (fieldName, value) => {
          if (fieldName === 'class') return 'diagnosis.epilepsy.etiology.opts.' + value;
          if (fieldName === 'subClasses' && Array.isArray(value))
            // TODO subclasses better localization, needs better locales
            return returnSimpleObjectArray(
              value,
              {
                subClassStructural: 'diagnosis.epilepsy.etiology.specificClass',
                //specificClass: 'diagnosis.epilepsy.etiology.',
                structuralOtherSpecification: 'diagnosis.epilepsy.etiology.determiner',
                //specificClassInput: 'diagnosis.epilepsy.etiology.',
                side: 'diagnosis.epilepsy.etiology.side',
                field: 'diagnosis.epilepsy.etiology.field',
                orphaCode: 'diagnosis.epilepsy.etiology.orpha',
                superSpecificClass: 'diagnosis.epilepsy.etiology.determiner',
              },
              {
                subClassStructural: val => 'diagnosis.epilepsy.etiology.opts.' + val,
                specificClass: val => 'diagnosis.epilepsy.etiology.opts.' + val,
                structuralOtherSpecification: val => 'diagnosis.epilepsy.etiology.opts.' + val,
                side: val => 'diagnosis.epilepsy.etiology.opts.' + val,
                field: val => 'diagnosis.epilepsy.etiology.opts.' + val,
                superSpecificClass: val => 'diagnosis.epilepsy.etiology.opts.' + val,
              },
            );
          return null;
        };
      }
      case 'surgicalTreatment': {
        return (fieldName, value) => {
          // Epilepsy
          // Todo operations
          // if (fieldName === 'operations' && Array.isArray(value))
          //   return returnSimpleObjectArray(
          //     value,
          //     {
          //       typeOfOperation: 'surgicalTreatment.operation.typeOfOperation.title'
          //     },
          //     { typeOfOperation: val => 'surgicalTreatment.operation.typeOfOperation.opts.' + val },
          //   );

          if (fieldName === 'engelClassification' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                classification: 'surgicalTreatment.engelClassification.title',
                timeFromOperation: 'surgicalTreatment.engelClassification.timeFromOperation.title',
              },
              {
                timeFromOperation: val => 'surgicalTreatment.engelClassification.timeFromOperation.opts.' + val,
              },
            );

          // Todo: hard to localize values
          // if (fieldName === 'complications' && Array.isArray(value))
          //   return returnSimpleObjectArray(
          //     value,
          //     {
          //       classification: 'surgicalTreatment.engelClassification.title',
          //       timeFromOperation: 'surgicalTreatment.engelClassification.timeFromOperation.title',
          //     },
          //     {
          //       timeFromOperation: val => 'surgicalTreatment.engelClassification.timeFromOperation.opts.' + val,
          //     },
          //   );

          if (fieldName === 'pads' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                findings: 'surgicalTreatment.pad.findings.title',
                subType: 'surgicalTreatment.pad.subType.title',
                otherInfo: 'surgicalTreatment.pad.otherInfo.header',
              },
              {
                findings: val => 'surgicalTreatment.pad.findings.opts.' + val,
                subType: val => 'surgicalTreatment.pad.subType.opts.' + val,
              },
            );

          // Sleep apnea
          if (fieldName === 'controlVisits' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                pleasedInformation: 'treatment.surgicalTreatment.controls.pleasedInformation',
                controlType: 'treatment.surgicalTreatment.controls.controlType',
                patientPleasedWithSurgery: 'treatment.surgicalTreatment.controls.patientPleasedWithSurgery',
                dayTimeTiredness: 'treatment.surgicalTreatment.controls.dayTimeTiredness',
                monitoringPolygraphyDone: 'treatment.surgicalTreatment.controls.monitoringPolygraphyDone',
                treatmentComplication: 'treatment.surgicalTreatment.controls.treatmentComplication',
                complicationInformation: 'treatment.surgicalTreatment.controls.complicationInformation',
              },
              {
                dayTimeTiredness: val => 'treatment.surgicalTreatment.controls.opts.' + val,
                controlType: val => 'treatment.surgicalTreatment.controls.opts.' + val,
              },
            );
          if (fieldName === 'controlVisitsJawSurgery' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                controlType: 'treatment.surgicalTreatment.controls.controlType',
                dayTimeTiredness: 'treatment.surgicalTreatment.controls.dayTimeTiredness',
              },
              {
                dayTimeTiredness: val => 'treatment.surgicalTreatment.controls.opts.' + val,
                controlType: val => 'treatment.surgicalTreatment.controls.opts.' + val,
              },
            );
          if (fieldName === 'knkType') return 'treatment.surgicalTreatment.opts.' + value;
          if (fieldName === 'operationType') return 'treatment.surgicalTreatment.opts.' + value;
          if (fieldName === 'bariatricSurgeryType') return 'treatment.surgicalTreatment.opts.' + value;
          if (fieldName === 'primaryOrReoperation') return 'treatment.surgicalTreatment.opts.' + value;
          if (fieldName === 'reoperationNeed') return 'treatment.surgicalTreatment.opts.' + value;

          return null;
        };
      }

      case 'neurologicalStatusAndEDSS': {
        return (fieldName, value) => {
          const edssFields = [
            'pyramidalFunctions',
            'cerebralFunctions',
            'brainstemFunctions',
            'sensoryFunctions',
            'bowelAndBladderFunctions',
            'visualAcuity',
            'cerebellarFunctions',
          ];
          if (edssFields.includes(fieldName) && value && typeof value === 'object' && 'score' in value) {
            return value['score'] as number;
          }
          return null;
        };
      }

      case 'sleepApneaSeverity': {
        return (fieldName, value) => {
          if (fieldName === 'severity') return 'diagnosis.sleepApnea.sleepApneaSeverity.opts.' + value;
          return null;
        };
      }

      case 'checklist': {
        return (fieldName, value) => {
          if (fieldName === 'date' || fieldName === 'treatmentsPolicyDoneOther') return null;
          if (Array.isArray(value)) return value.map(v => 'checklist.opts.' + v);
          return null;
        };
      }

      case 'oxygenTherapy': {
        return (fieldName, value) => {
          if (
            fieldName === 'treatmentStart' ||
            fieldName === 'oxygenTherapyType' ||
            fieldName === 'treatmentStartLocation'
          ) {
            return 'treatment.oxygenTherapy.opts.' + value;
          }

          if (fieldName === 'endReason' && Array.isArray(value)) {
            return value.map(v => 'treatment.opts.oxygenTherapyEndReason.' + v);
          }
          if (fieldName === 'treatmentResponses' && Array.isArray(value)) {
            return returnSimpleObjectArray(
              value,
              {
                oxygenSaturation: 'treatment.oxygenTherapy.treatmentResponses.oxygenSaturation',
              },
              {},
            );
          }
          if (fieldName === 'settings' && Array.isArray(value)) {
            return returnSimpleObjectArray(
              value,
              {
                additionalInformation: 'treatment.oxygenTherapy.settings.additionalInformation',
                implementationOfTreatment: 'treatment.oxygenTherapy.settings.implementationOfTreatment',
                flowMin: 'flowMin',
                flowMax: 'flowMax',
                humidifierInUse: 'treatment.oxygenTherapy.settings.humidifierInUse',
              },
              { implementationOfTreatment: val => 'treatment.oxygenTherapy.opts.' + val },
            );
          }

          return null;
        };
      }

      case 'papTherapy': {
        return (fieldName, value) => {
          // Todo better localizations for array objects
          if (fieldName === 'treatmentStartLocation') {
            return 'treatment.papTherapy.opts.' + value;
          }

          if (fieldName === 'endReason' && Array.isArray(value)) {
            return value.map(v => 'treatment.opts.papTherapyEndReason.' + v);
          }
          if (fieldName === 'treatmentResponses' && Array.isArray(value)) {
            return returnSimpleObjectArray(value, {}, {});
          }
          if (fieldName === 'devices' && Array.isArray(value)) {
            return returnSimpleObjectArray(value, {}, {});
          }
          if (fieldName === 'implementationMethods' && Array.isArray(value)) {
            return returnSimpleObjectArray(
              value,
              { additionalInformation: 'treatment.papTherapy.additionalInformation' },
              {},
            );
          }
          if (fieldName === 'settings' && Array.isArray(value)) {
            return returnSimpleObjectArray(value, {}, {});
          }

          return null;
        };
      }

      case 'madTherapy': {
        return (fieldName, value) => {
          if (fieldName === 'treatmentStartLocation') {
            return 'treatment.madTherapy.opts.' + value;
          }
          if (fieldName === 'endReason' && Array.isArray(value)) {
            return value.map(v => 'treatment.opts.madTherapyEndReason.' + v);
          }

          if (fieldName === 'controls' && Array.isArray(value)) {
            return returnSimpleObjectArray(
              value,
              {
                controlType: 'treatment.madTherapy.controls.controlType',
                fatiqueDuringTheDay: 'treatment.madTherapy.controls.fatiqueDuringTheDay',
                adverseEffects: 'treatment.madTherapy.controls.adverseEffects',
                details: 'treatment.madTherapy.controls.details',
                deviceAdjustment: 'treatment.madTherapy.controls.deviceAdjustment',
                madUsageFrequency: 'treatment.madTherapy.controls.madUsageFrequency',
              },
              {
                controlType: val => 'treatment.madTherapy.controls.opts.' + val,
                fatiqueDuringTheDay: val => 'treatment.madTherapy.controls.opts.' + val,
                adverseEffects: val => 'treatment.madTherapy.controls.opts.' + val,
                deviceAdjustment: val => 'treatment.madTherapy.controls.opts.' + val,
                madUsageFrequency: val => 'treatment.madTherapy.controls.opts.' + val,
              },
            );
          }

          return null;
        };
      }

      case 'hfncTherapy': {
        return (fieldName, value) => {
          if (fieldName === 'treatmentStart' || fieldName === 'treatmentStartLocation')
            return 'treatment.hfncTherapy.opts.' + value;
          if (fieldName === 'endReason' && Array.isArray(value))
            return value.map(val => 'treatment.opts.hfncTherapyEndReason.' + val);
          if (fieldName === 'settings' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                flowRate: 'treatment.hfncTherapy.settings.flowRate',
                supplementalOxygen: 'treatment.hfncTherapy.settings.supplementalOxygen',
                HFNCTreatmentType: 'treatment.hfncTherapy.settings.HFNCTreatmentType',
                oxygenFlow: 'treatment.hfncTherapy.settings.oxygenFlow',
                time: 'general.time',
              },
              { time: val => (isTime(val) ? val : '-') }, // TODO not working
            );
          return null;
        };
      }

      case 'notes': {
        return (fieldName, value) => {
          if (fieldName === 'neuroModulationWhichOtherDevice') return 'notes.opts.' + value;
          if (fieldName === 'neuroModulationActiveTreatment') return 'notes.opts.' + value;
          return null;
        };
      }

      case 'madrs': {
        return (fieldName, value) => {
          if (value === 0) return '0';
          return null;
        };
      }

      case 'contraIndicationsToTreatment': {
        return (fieldName, value) => {
          const arrayFields = [
            'tmsAbsoluteContraIndications',
            'tmsRelativeContraIndications',
            'dcsAbsoluteContraIndications',
          ];
          if (arrayFields.includes(fieldName) && Array.isArray(value))
            return value.map(val => 'diagnosis.ninmt.contraIndicationsToTreatment.opts.' + val);
          return null;
        };
      }

      case 'symptomsAndLocations': {
        return (fieldName, value) => {
          const symptomFields = ['symptomLocation', 'symptom'];
          if (symptomFields.includes(fieldName)) return 'diagnosis.ninmt.symptomsAndLocations.opts.' + value;
          return null;
        };
      }

      case 'rtms': {
        return (fieldName, value) => {
          if (fieldName === 'adverseEffects' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                additionalInformation: 'rtms.additionalInformation',
                adverseEffects: 'rtms.adverseEffects',
              },
              { adverseEffects: val => 'rtms.opts.adverseEffects.' + val },
            );
          if (fieldName === 'complications' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                additionalInformation: 'rtms.additionalInformation',
                complications: 'rtms.complications',
              },
              { complications: val => 'rtms.opts.complications.' + val },
            );
          if (fieldName === 'unusedSessions' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                reason: 'rtms.unusedSessionReason',
                reasonDetails: 'rtms.unusedSessionReasonDetails',
                reasonDetailsOther: 'rtms.unusedSessionReasonDetailsOther',
                createDate: null,
              },
              {
                reason: val => 'rtms.opts.unusedSessionReason.' + val,
                reasonDetails: val => 'rtms.opts.unusedSessionReasonDetails.' + val,
              },
            );
          return null;
        };
      }

      case 'tdcs': {
        return (fieldName, value) => {
          if (fieldName === 'otherEvents' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                details: 'tdcs.additionalInformation',
                type: 'tdcs.typeOfEvent',
                createDate: null,
              },
              { type: val => 'tdcs.opts.eventTypes.' + val },
            );
          if (fieldName === 'adverseEffects' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                additionalInformation: 'tdcs.additionalInformation',
                adverseEffects: 'tdcs.adverseEffects',
              },
              { adverseEffects: val => 'tdcs.opts.adverseEffects.' + val },
            );
          if (fieldName === 'complications' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                additionalInformation: 'tdcs.additionalInformation',
                complications: 'tdcs.complications',
              },
              { complications: val => 'tdcs.opts.complications.' + val },
            );
          if (fieldName === 'unusedSessions' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                reason: 'tdcs.unusedSessionReason',
                reasonDetails: 'tdcs.unusedSessionReasonDetails',
                reasonDetailsOther: 'tdcs.unusedSessionReasonDetailsOther',
                createDate: null,
              },
              {
                reason: val => 'tdcs.opts.unusedSessionReason.' + val,
                reasonDetails: val => 'tdcs.opts.unusedSessionReasonDetails.' + val,
              },
            );
          return null;
        };
      }

      case 'uhdrsMotorRatingScale': {
        return (fieldName, value) => {
          if (
            (fieldName === 'totalScoreOnly' || fieldName === 'shortTest') &&
            Array.isArray(value) &&
            value.length === 1
          )
            return 'general.' + value[0];
          return null;
        };
      }

      case 'pbaS': {
        return (fieldName, value) => {
          if ((fieldName === 'hallucinationsModality' || fieldName === 'shortTest') && Array.isArray(value))
            return value.map(val => 'behaviour.opts.pbaS.modality.' + val);

          if (fieldName === 'isInformantRelative') return 'behaviour.pbaS.opts.' + value;
          return null;
        };
      }

      case 'uhdrsFunctionalAssessment': {
        return (fieldName, value) => {
          if (fieldName === 'informationalSources')
            return 'functionalPerformance.opts.uhdrsFunctionalAssessment.' + value;

          return null;
        };
      }

      case 'uhdrsFunctionalCapacity': {
        return (fieldName, value) => {
          if (fieldName === 'informationalSources')
            return 'functionalPerformance.opts.uhdrsFunctionalCapacity.' + value;

          return null;
        };
      }

      case 'parkinsonSymptoms': {
        return (fieldName, value) => {
          if (fieldName === 'parkinsonEarlySymptom' && Array.isArray(value))
            return value.map(val => 'diagnosis.parkinsonEarlySymptom.' + val);
          if (fieldName === 'parkinsonMotoricSymptomOnset')
            return 'diagnosis.parkinsonMotoricSymptomOnset.opts.' + value;
          return null;
        };
      }

      case 'msSymptoms': {
        return (fieldName, value) => {
          if (fieldName === 'msSymptoms' && Array.isArray(value))
            return value.map(val => 'diagnosis.msSymptom.opts.' + val);
          if (fieldName === 'msSymptomLevel' && Array.isArray(value))
            return value.map(val => 'diagnosis.msSymptomLevel.opts.' + val);
          if (fieldName === 'msOriginOfInformation') return 'diagnosis.msOriginOfInformation.opts.' + value;
          return null;
        };
      }

      case 'mgravisSymptoms': {
        return (fieldName, value) => {
          if (fieldName === 'symptomLocation' && Array.isArray(value))
            return value.map(val => 'diagnosis.mgravis.symptoms.' + val);
          return null;
        };
      }

      case 'respiratoryFirstVisit':
      case 'sleepApneaFirstVisit': {
        return (fieldName, value) => {
          if (fieldName === 'referralOrAcute') return 'diagnosis.sleepApnea.sleepApneaFirstVisit.opts.' + value;
          return null;
        };
      }

      case 'huntingtonSymptoms': {
        return (fieldName, value) => {
          if (fieldName === 'symptoms') return 'diagnosis.huntingtonSymptom.opts.' + value;
          return null;
        };
      }

      case 'indicationForTreatment': {
        return (fieldName, value) => {
          if (fieldName === 'painSpecification' || fieldName === 'neuropathicPainFindingCertainty')
            return 'diagnosis.ninmt.indicationForTreatment.opts.' + value;

          if (fieldName === 'indication' && Array.isArray(value))
            return value.map(val => 'diagnosis.ninmt.indicationForTreatment.opts.' + val);

          if (fieldName === 'neuropathicPainFinding' && Array.isArray(value))
            return value.map(val => 'diagnosis.ninmt.indicationForTreatment.opts.' + val);

          if (
            fieldName === 'painICD10' &&
            value &&
            typeof value === 'object' &&
            'icd10code' in value &&
            'icd10description' in value
          )
            return value['icd10code'] + ' - ' + value['icd10description'];

          return null;
        };
      }

      case 'htt': {
        return (fieldName, value) => {
          if (fieldName === 'httHaplotype') return 'diagnosis.huntington.htt.opts.httHaplotype.' + value;
          if (fieldName === 'status') return 'diagnosis.huntington.htt.opts.doneNotDoneUnknown.' + value;

          return null;
        };
      }

      case 'medication': {
        return (fieldName, value) => {
          if (fieldName === 'administration' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                doseQuantity: 'medication.administration.doseQuantity',
                doseQuantityUnit: 'medication.administration.doseQuantityUnit',
                isExtraDose: 'medication.administration.isExtraDose',
                additionalInformation: 'medication.administration.additionalInformation',
                startTime: 'medication.administration.startTime',
                endTime: 'medication.administration.endTime',
              },
              {},
            );

          if (fieldName === 'pauses' && Array.isArray(value))
            return returnSimpleObjectArray(
              value,
              {
                pausationReason: 'medication.pausationReason',
                startTime: 'medication.startTime',
                endTime: 'medication.endedTime',
                additionalInfo: 'medication.additionalInformation',
              },
              {
                pausationReason: val => 'medication.pausationReasons.' + val,
              },
            );
          if (fieldName === 'endReason' && Array.isArray(value)) return value.map(val => 'medication.opts.' + val);
          if (fieldName === 'endReasonPrimary') return 'medication.opts.' + value;
          if (fieldName === 'isClinicalStudy' && Array.isArray(value))
            return value[0] ? 'general.true' : 'general.false';
          return null;
        };
      }

      case 'ariaSymptoms': {
        return (fieldName, value) => {
          if (fieldName === 'symptoms' && Array.isArray(value)) return value.map(sy => 'ariaSymptoms.opts.' + sy);
          return null;
        };
      }

      case 'adverseEffect': {
        return (fieldName, value) => {
          if (fieldName === 'adverseEffects' && Array.isArray(value))
            return value.map(val => {
              const valueIsAdverseEffect = (val: unknown): val is { eventName: string; targetOrgan: string } => {
                if (
                  val &&
                  typeof val === 'object' &&
                  'eventName' in val &&
                  typeof val.eventName === 'string' &&
                  'targetOrgan' in val &&
                  typeof val.targetOrgan === 'string'
                )
                  return true;
                return false;
              };

              const eventName: string = valueIsAdverseEffect(val) ? val['eventName'] : '';
              const targetOrgan: string = valueIsAdverseEffect(val) ? val['targetOrgan'] : '';

              return `(${targetOrgan} - ${eventName})`;
            });
          return null;
        };
      }

      case 'ftmtrs': {
        return (fieldName, value) => {
          if (fieldName === 'whichHand') return 'updrs.opts.ftmtrs.whichHand.' + value;
          return null;
        };
      }

      case 'filestorefile': {
        return (fieldName, value) => {
          if (fieldName === 'date' && typeof value === 'string')
            return formatPartialDate(partialDateFromDate(new Date(value)));

          if (fieldName === 'subType' && typeof value === 'string') {
            if (value === 'rtms') return 'rtms.title';
            if (value === 'painAndSymptomMap') return 'patientFiles.painAndSymptomMap';
            return value;
          }
          return null;
        };
      }

      default: {
        return null;
      }
    }
  };

  return (fieldName: string, value: TFieldValue) => {
    const valueThisDocType = map(docType)?.(fieldName, value);
    if (valueThisDocType) return valueThisDocType;
    if (!valueThisDocType) {
      return genericOptionValues(value, fieldName);
    }
    return null;
  };
};

export const genericOptionValues = (value: TFieldValue, fieldName?: string) => {
  if (value === 'yes' || value === true) return 'general.yes';
  if (value === 'no' || value === false) return 'general.no';
  if (value === 'unknown') return 'general.unknown';
  if (value === 'left') return 'general.left';
  if (value === 'right') return 'general.right';
  if (fieldName === 'hasEnded' && Array.isArray(value))
    return value[0] === true ? 'general.yes' : value[0] === false ? 'general.no' : null;

  return null;
};

export const returnDefaultValues = (value: TFieldValue) => {
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (isPartialDate(value)) return formatPartialDate(value);
  if (isTime(value)) return formatTime(value);
  return null;
};
